'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

export interface CartItem {
  id: string;
  productId: string;
  title: string;
  sku?: string;
  imageUrl?: string;
  quantity: number;
  unitPriceMinor: number;
  totalPriceMinor: number;
  sellerId?: string;
  sellerName?: string;
  hubSlug?: string;
  vendorOrgId?: string;
  fitmentMetadata?: {
    make?: string;
    model?: string;
    year?: number | string;
    vin?: string;
  } | null;
}

export interface VendorGroup {
  sellerId: string;
  sellerName: string;
  hubSlug: string;
  items: CartItem[];
  vendorSubtotalMinor: number;
  vendorShippingMinor: number;
}

export interface CartData {
  id: string;
  countryCode: string;
  currency: string;
  couponCode?: string | null;
  discountMinor: number;
  subtotalMinor: number;
  shippingTotalMinor: number;
  totalMinor: number;
  itemCount: number;
  vendorCount: number;
  vendorGroups: VendorGroup[];
}

interface CartContextType {
  cart: CartData | null;
  itemCount: number;
  loading: boolean;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  addToCart: (product: any, quantity?: number, fitment?: any) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  applyCoupon: (code: string) => Promise<boolean>;
  refreshCart: () => Promise<void>;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'https://parts4drive.com';

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<CartData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const getSessionId = useCallback(() => {
    if (typeof window === 'undefined') return '';
    let sid = localStorage.getItem('p4d_cart_session_id');
    if (!sid) {
      sid = `ses_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('p4d_cart_session_id', sid);
    }
    return sid;
  }, []);

  const refreshCart = useCallback(async () => {
    try {
      const sid = getSessionId();
      if (!sid) return;

      const cartId = typeof localStorage !== 'undefined' ? localStorage.getItem('p4d_active_cart_id') : null;
      let url = `${API_BASE}/api/v1/cart?session_id=${encodeURIComponent(sid)}`;
      if (cartId) url += `&cart_id=${encodeURIComponent(cartId)}`;

      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.cart) {
          setCart(json.cart);
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('p4d_active_cart_id', json.cart.id);
          }
        }
      }
    } catch (err) {
      console.warn('Failed to fetch cart:', err);
    } finally {
      setLoading(false);
    }
  }, [getSessionId]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = async (product: any, quantity: number = 1, fitment: any = null) => {
    try {
      const sid = getSessionId();
      const currentCartId = cart?.id || (typeof localStorage !== 'undefined' ? localStorage.getItem('p4d_active_cart_id') : null);

      const priceMinor = product.priceMinor || 
                         (product.price ? Math.round(parseFloat(product.price) * 100) : 0) ||
                         product.price_in_cents || 0;

      const payload = {
        cartId: currentCartId,
        sessionId: sid,
        productId: product.id,
        sellerId: product.sellerId || product.seller_id,
        vendorOrgId: product.vendorOrgId || product.org_id,
        hubSlug: product.hubSlug || product.hub_slug || 'commercial-hub',
        quantity,
        unitPriceMinor: priceMinor,
        currency: product.currency || cart?.currency || 'NGN',
        countryCode: product.countryCode || product.country || cart?.countryCode || 'NG',
        fitmentMetadata: fitment || {
          make: product.make,
          model: product.model,
          year: product.year
        }
      };

      const res = await fetch(`${API_BASE}/api/v1/cart/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && json.success) {
        toast.success(`🛒 Added "${product.title || product.name || 'Part'}" to cart!`);
        await refreshCart();
        setIsDrawerOpen(true);
      } else {
        toast.error(json.error || 'Failed to add item to cart');
      }
    } catch (err: any) {
      toast.error(`Error adding to cart: ${err.message}`);
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/cart/items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity })
      });

      if (res.ok) {
        await refreshCart();
      } else {
        const json = await res.json();
        toast.error(json.error || 'Failed to update quantity');
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const removeItem = async (itemId: string) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/cart/items/${itemId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Item removed from cart');
        await refreshCart();
      } else {
        toast.error('Failed to remove item');
      }
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const applyCoupon = async (code: string): Promise<boolean> => {
    if (!cart?.id || !code.trim()) return false;
    try {
      const res = await fetch(`${API_BASE}/api/v1/cart/apply-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cartId: cart.id,
          couponCode: code.trim()
        })
      });

      const json = await res.json();
      if (res.ok && json.success) {
        toast.success(json.message || 'Coupon applied successfully!');
        await refreshCart();
        return true;
      } else {
        toast.error(json.error || 'Invalid coupon code');
        return false;
      }
    } catch (err: any) {
      toast.error(err.message);
      return false;
    }
  };

  const clearCart = () => {
    setCart(null);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('p4d_active_cart_id');
    }
  };

  const itemCount = cart?.itemCount || 0;

  return (
    <CartContext.Provider
      value={{
        cart,
        itemCount,
        loading,
        isDrawerOpen,
        openDrawer: () => setIsDrawerOpen(true),
        closeDrawer: () => setIsDrawerOpen(false),
        addToCart,
        updateQuantity,
        removeItem,
        applyCoupon,
        refreshCart,
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
